<script setup lang="ts">
import { ref } from 'vue'

import CarouselScrollbar from '../../emblaCarousel/CarouselScrollbar.vue'
import InputFooter from '@satch-ui/components/input/InputFooter.vue'
import { t } from '@satch-ui/translations/translations'
import CarouselItem from '@shared/fondof-ui/components/molecules/carousel/CarouselItem.vue'
import CarouselRoot from '@shared/fondof-ui/components/molecules/carousel/CarouselRoot.vue'
import CarouselContainer from '@shared/fondof-ui/components/molecules/carousel/CarouselScrollContainer.vue'
import CarouselViewport from '@shared/fondof-ui/components/molecules/carousel/CarouselViewport.vue'
import { addToCart } from '@shared/global-actions/actions/cart'
import type { ProductWithVariants } from '@shared/shop-api/product/types'
import CarouselPagination from '@storefront-satch/components/molecules/emblaCarousel/CarouselPagination.vue'
import CarouselPrevNextButton from '@storefront-satch/components/molecules/emblaCarousel/CarouselPrevNextButton.vue'
import ProductCard from '@storefront-satch/components/molecules/product/card/ProductCard.vue'
import ProductCardContent from '@storefront-satch/components/molecules/product/card/ProductCardContent.vue'
import ProductImage from '@storefront-satch/components/molecules/product/card/ProductImage.vue'
import type { ProductCarouselProps } from '@storefront-satch/components/molecules/product/carousel/types'

import { LoaderIcon } from '@fond-of/satch-icons'

const props = withDefaults(defineProps<ProductCarouselProps>(), {
  quantity: 1,
  showAtcButton: true,
  showWishlistAddButton: false,
  buttonsPosition: 'inside',
})

const error = ref<string>()

const addProductToCart = async (product: ProductWithVariants) => {
  try {
    error.value = undefined

    await addToCart(product.variants[0].id, props.quantity, {
      beforeTriggerIncreaseCartLineEvent:
        props.onBeforeTriggerIncreaseCartLineEvent,
      showSidePanel: props.showSidePanel,
      attributes: props.cartLineAttributes,
    })
  } catch (exception) {
    error.value = t('validation.addToCart')
  }
}
</script>

<template>
  <CarouselRoot
    v-if="props.products && props.products.length"
    :class="props.class"
    :options="{
      breakpoints: {
        '(min-width: 640px)': {
          dragFree: false,
        },
      },
      dragFree: true,
      align: 'start',
      containScroll: 'trimSnaps',
      slidesToScroll: 'auto',
      prevNextButtons: true,
      pagination: true,
      mobileScrollbar: true,
    }"
  >
    <CarouselViewport>
      <CarouselContainer class="px-5 sm:px-0">
        <CarouselItem
          v-for="product in props.products"
          :key="product.id"
          class="mr-2.5 w-[calc(50%-10px)] sm:mr-5 sm:w-[calc(25%-20px)]"
        >
          <ProductCard
            :product="product"
            :open-in-new-tab="false"
            :show-wishlist-add-button="props.showWishlistAddButton"
            :variant="props.variant"
          >
            <template #image>
              <ProductImage
                :product="product"
                :alternate-images="false"
              />
            </template>

            <template #content="slotProps">
              <ProductCardContent
                v-bind="slotProps"
                :product="product"
                :show-atc-button="props.showAtcButton"
                :variant="props.variant"
                :available="product.available"
                :is-coming-soon-launch="product.isComingSoonLaunch"
                :is-coming-soon-restock="product.isComingSoonRestock"
                :is-coming-soon="product.isComingSoon"
                @button-click="() => addProductToCart(product)"
              />
            </template>
          </ProductCard>
        </CarouselItem>
      </CarouselContainer>

      <CarouselPrevNextButton type="prev" />
      <CarouselPrevNextButton type="next" />
    </CarouselViewport>

    <CarouselPagination class="!mt-10" />
    <CarouselScrollbar class="px-5 pt-8 sm:px-0" />
  </CarouselRoot>

  <InputFooter
    v-if="showAtcButton"
    :error="error"
  />

  <div
    v-if="!props.products"
    class="mx-auto"
  >
    <LoaderIcon class="sat:animate-spin size-16" />
  </div>
</template>
